import gsap from 'gsap';
import { CUSTOM_EVENTS } from '../lib/constants';

type AccordionInstance = {
  isOpen: boolean;
  openAccordion: () => void;
  closeAccordion: () => void;
};

let currentlyOpenAccordion: HTMLElement | null = null;

const accordionContainers = document.querySelectorAll(
  '[data-accordion-with-preview-container]'
);

function accordionWithPreview(el: HTMLElement) {
  const openButton = el.querySelector<HTMLElement>(
    '[data-accordion-with-preview-head]'
  );
  const closeButton = el.querySelector<HTMLElement>(
    '[data-accordion-with-preview-close]'
  );
  const content = el.querySelector<HTMLElement>(
    '[data-accordion-with-preview-content]'
  );

  if (!openButton || !closeButton || !content) return;

  let isOpen = false;

  const disableFocusableElements = () => {
    const focusableElements = content.querySelectorAll<HTMLElement>(
      'a, button, [tabindex]'
    );
    focusableElements.forEach(element => {
      element.setAttribute('tabindex', '-1');
    });
  };

  const enableFocusableElements = () => {
    const focusableElements = content.querySelectorAll<HTMLElement>(
      'a, button, [tabindex]'
    );
    focusableElements.forEach(element => {
      element.removeAttribute('tabindex');
    });
  };

  const closeAccordion = () => {
    if (!isOpen) return;

    isOpen = false;

    el.classList.remove('open');

    gsap.to(content, {
      height: 0,
      duration: 0.4,
    });

    openButton.classList.remove('is-hidden');
    closeButton.classList.add('is-hidden');

    openButton.setAttribute('aria-expanded', 'false');
    closeButton.setAttribute('aria-expanded', 'false');

    disableFocusableElements();
    currentlyOpenAccordion = null;
  };

  const openAccordion = () => {
    if (isOpen) return;

    if (currentlyOpenAccordion && currentlyOpenAccordion !== el) {
      const currentlyOpenInstance = (
        currentlyOpenAccordion as HTMLElement & {
          accordionInstance: { closeAccordion: () => void };
        }
      ).accordionInstance;
      if (currentlyOpenInstance) {
        currentlyOpenInstance.closeAccordion();
      }
    }

    isOpen = true;
    currentlyOpenAccordion = el;

    el.classList.add('open');

    gsap.to(content, {
      height: content.scrollHeight,
      duration: 0.4,
      onComplete: () => {
        gsap.set(content, { height: 'auto' });
      },
    });

    openButton.classList.add('is-hidden');
    closeButton.classList.remove('is-hidden');

    openButton.setAttribute('aria-expanded', 'true');
    closeButton.setAttribute('aria-expanded', 'true');

    enableFocusableElements();
  };

  disableFocusableElements();

  (
    el as unknown as { accordionInstance: AccordionInstance }
  ).accordionInstance = { isOpen, openAccordion, closeAccordion };

  openButton.addEventListener('click', openAccordion);
  closeButton.addEventListener('click', closeAccordion);

  el.addEventListener(CUSTOM_EVENTS.scrolledTo, () => {
    if (!isOpen) openAccordion();
  });
}

accordionContainers.forEach(container => {
  const accordionItems = container.querySelectorAll(
    '[data-accordion-with-preview]'
  );
  accordionItems.forEach(item => accordionWithPreview(item as HTMLElement));
});
